<template>
	<div class="linkedin-company-id-input">
		<div v-if="isEditing">
			<!-- Label with tooltip -->
			<div class="flex items-center gap-2 mb-2">
				<label
					class="mb-0 font-sans text-sm font-normal text-gray-700 normal-case"
				>
					{{
						$t('job-offer.activate-modal.linkedin-company-id-label')
					}}
				</label>
				<div class="relative inline-block">
					<Icon
						:icon="['far', 'info-circle']"
						class="text-gray-400 cursor-help"
						v-tooltip:top="
							$t(
								'job-offer.activate-modal.linkedin-company-id-tooltip'
							)
						"
					/>
				</div>
			</div>

			<!-- Edit mode -->
			<div class="flex items-start gap-2">
				<div class="flex-1">
					<input
						ref="input"
						type="text"
						v-model="localValue"
						:placeholder="
							$t(
								'job-offer.activate-modal.linkedin-company-id-placeholder'
							)
						"
						class="w-full h-10 px-3 border rounded-lg placeholder:text-gray-500 focus:ring-black focus:outline-none"
						:class="[
							hasError ? '!border-red-300' : '!border-gray-200'
						]"
						@input="handleInput"
						@blur="handleBlur"
					/>
					<p
						v-if="validationMessage"
						class="mt-1 text-sm text-red-600"
					>
						{{ validationMessage }}
					</p>
					<p v-else class="mt-1 text-sm text-gray-500">
						{{
							$t(
								'job-offer.activate-modal.linkedin-optional-text'
							)
						}}
					</p>
				</div>

				<!-- Save button -->
				<Button
					type="secondary"
					@click="handleSave"
					:disabled="hasError"
				>
					{{ $t('crud.save') }}
				</Button>
			</div>
		</div>

		<!-- Display mode -->
		<div v-else class="flex items-center gap-2">
			<div
				class="flex items-center justify-between flex-1 h-10 pl-3 rounded-lg bg-neutral-100"
			>
				<span v-if="value" class="text-gray-900">{{
					displayValue
				}}</span>
				<span v-else class="text-gray-400">
					{{
						$t(
							'job-offer.activate-modal.linkedin-company-id-placeholder'
						)
					}}
				</span>

				<!-- Edit button -->
				<Button type="tertiary" icon="pen" @click="enterEditMode">
				</Button>
			</div>
		</div>
	</div>
</template>

<script>
import Button from '~/components/ui/common/Button'
import Icon from '~/components/ui/common/Icon'
import {
	extractLinkedInCompanyId,
	formatLinkedInCompanyUrl
} from '~/utils/linkedinCompany'

export default {
	name: 'LinkedInCompanyIdInput',
	components: {
		Button,
		Icon
	},
	props: {
		value: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			localValue: '',
			isEditing: false,
			validationMessage: null,
			hasError: false,
			hasSaved: false
		}
	},
	computed: {
		displayValue() {
			if (!this.value) return ''
			return this.formatLinkedInUrl(this.value)
		}
	},
	watch: {
		value(newValue) {
			if (this.isEditing) {
				return
			}
			this.localValue = this.formatLinkedInUrl(newValue)
		}
	},
	mounted() {
		// Start in edit mode if no value is set
		if (!this.value) {
			this.isEditing = true
		} else {
			this.localValue = this.formatLinkedInUrl(this.value)
		}
	},
	methods: {
		enterEditMode() {
			this.isEditing = true
			this.localValue = this.formatLinkedInUrl(this.value)
			this.$emit('edit')
			this.$nextTick(() => {
				if (this.$refs.input) {
					this.$refs.input.focus()
				}
			})
		},

		handleInput() {
			// Validate on input
			this.validate(this.localValue, false)
		},

		handleBlur() {
			// Don't auto-save on blur, user must click save button
			this.validate(this.localValue, true)
		},

		handleSave() {
			const trimmedValue = this.localValue ? this.localValue.trim() : ''

			if (!trimmedValue) {
				this.clearValue()
				return
			}

			if (this.validate(this.localValue, true)) {
				const companyId = this.extractCompanyId(this.localValue)
				if (companyId) {
					const formattedValue = this.formatLinkedInUrl(companyId)
					this.$emit('input', formattedValue)
					this.$emit('validation', true)
					this.$emit('save', companyId)
					this.localValue = formattedValue
					this.isEditing = false
					this.hasSaved = true
				}
			}
		},

		clearValue() {
			this.validationMessage = null
			this.hasError = false
			this.localValue = ''
			this.$emit('input', '')
			this.$emit('validation', false)
			this.$emit('save', null)
			this.isEditing = false
			this.hasSaved = true
		},

		formatLinkedInUrl(value) {
			return formatLinkedInCompanyUrl(value)
		},

		extractCompanyId(url) {
			return extractLinkedInCompanyId(url)
		},

		validate(url, showMessage = true) {
			if (!url || url.trim() === '') {
				this.validationMessage = null
				this.hasError = false
				this.$emit('validation', true)
				return true
			}

			const companyId = this.extractCompanyId(url)

			if (!companyId) {
				if (showMessage) {
					this.validationMessage = this.$t(
						'job-offer.activate-modal.linkedin-company-id-invalid'
					)
					this.hasError = true
				}
				this.$emit('validation', false)
				return false
			}

			// Valid
			this.validationMessage = null
			this.hasError = false
			this.$emit('validation', true)
			return true
		}
	}
}
</script>
